import { ActionButton, Checkbox, CheckboxGroup, Radio, RadioGroup, Text } from '@adobe/react-spectrum';
import Add from '@spectrum-icons/workflow/Add';
import pt from 'prop-types';
import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';

import { ErrorBoundary } from '@exchange-frontends/components';
import { azSort } from '@exchange-frontends/utils';

import { CheckboxGroupContainer } from './SideBar';

// Styled Components

/**
 * Component used to wrap the filter name and add ellipsis if the name is too long.
 * It also aligns the count to the right.
 */
const FilterName = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 160px;

  & + span {
    width: 30px;
    margin-left: 5px;
    text-align: right;
  }
`;

// Helper Components

/**
 * This component is used with the ErrorBoundary to display a null component when an error occurs while loading a filter entry, basically hiding that entry.
 * @returns {null} null
 */
const NullErrorComponent = () => null;

// Main Component

/**
 * This component is used in the Sidebar to display groups of filters.
 * @returns {React.Component}
 */
const FilterGroup = (props) => {
  const {
    filters,
    value,
    onChange,
    maxFilters = filters?.length,
    loading,
    type,
    shouldShowCount,
    shouldShowEllipsis,
    shouldUseKeyAsInputValue,
    skipSort,
    ...rest
  } = props;
  const [showAll, setShowAll] = useState(maxFilters >= filters?.length);
 
  // Calculate filters that are hidden due to maxFilters limit
  const hiddenFilters = useMemo(() => {
    if (!filters || filters.length <= maxFilters) return [];
    return filters.slice(maxFilters);
  }, [filters, maxFilters]);

  // Check if any selected value is in the hidden filters
  const hasHiddenSelection = useMemo(() => {
    if (!value || hiddenFilters.length === 0) return false;
    
    const getFilterValueForComparison = (filter) => shouldUseKeyAsInputValue ? filter.key : filter.name;
    
    if (type === 'checkbox' && Array.isArray(value)) {
      return value.some(selectedValue => 
        hiddenFilters.some(filter => selectedValue === getFilterValueForComparison(filter))
      );
    }
    return hiddenFilters.some(filter => value === getFilterValueForComparison(filter));
  }, [value, hiddenFilters, type, shouldUseKeyAsInputValue]);
  
  // Automatically expand the filter list when a selected filter is hidden
  useEffect(() => {
    if (!showAll && hasHiddenSelection) {
      setShowAll(true);
    }
  }, [hasHiddenSelection, showAll]);
  
  const shouldDisplayShowMoreButton = !showAll;
  const shownFilters = useMemo(() => {
    let sortedFilters;
    
    // If skipSort is true, maintain the original order of filters
    if (skipSort) {
      sortedFilters = [...filters];
    } else {
      // Otherwise apply the default sorting logic
      sortedFilters = filters.sort((a, b) => {
        if (a.name === 'Other') {
          return 1;
        }
        if (b.name === 'Other') {
          return -1;
        }
        if (a.key === 'All') {
          return -1;
        }
        if (b.key === 'All') {
          return 1;
        }
        return azSort(a.name, b.name);
      });
    }
    
    if (showAll) {
      return sortedFilters;
    }
    return sortedFilters.slice(0, maxFilters);
  }, [showAll, filters, maxFilters, skipSort]);

  const InputGroup = type === 'checkbox' ? CheckboxGroup : RadioGroup;
  const Input = type === 'checkbox' ? Checkbox : Radio;

  return (
    <CheckboxGroupContainer>
      <InputGroup
        value={value}
        onChange={(selectedOptions) => {
          onChange(selectedOptions);
        }}
        {...rest}
      >
        {shownFilters?.map(({ name, count, key }, idx) => (
          <ErrorBoundary errorComponent={<NullErrorComponent />} key={name ?? idx}>
            {() =>
              type === 'checkbox' && key === 'All' ? null : (
                <Input value={(shouldUseKeyAsInputValue ? key : name) ?? 'default'} isDisabled={loading}>
                  {shouldShowEllipsis ? <FilterName>{name}</FilterName> : name}
                  {shouldShowCount && <Text>({count ?? 0})</Text>}
                </Input>
              )
            }
          </ErrorBoundary>
        ))}
        {shouldDisplayShowMoreButton && (
          <ActionButton marginTop="size-100" onPress={() => setShowAll(true)}>
            <Add marginStart="size-100" />
            <Text>{`Show ${filters?.length - maxFilters ?? 0} more`}</Text>
          </ActionButton>
        )}
      </InputGroup>
    </CheckboxGroupContainer>
  );
};

FilterGroup.defaultProps = {
  type: 'checkbox',
  shouldShowCount: true,
  shouldShowEllipsis: true,
  shouldUseKeyAsInputValue: false,
  skipSort: false,
};

FilterGroup.propTypes = {
  /** Filter array for a specific key, taken from the zustand store. */
  filters: pt.arrayOf(pt.shape({ name: pt.string, count: pt.number })).isRequired,
  /** The currently selected values of the group.  */
  value: pt.oneOfType([pt.string, pt.arrayOf(pt.string).isRequired]),
  /** Callback for when the value changes. It will be called with the new selected options. */
  onChange: pt.func.isRequired,
  /** The maximum number of filters to display initially. Defaulted to display all filters */
  maxFilters: pt.number,
  /** Whether the filters are currently loading. */
  loading: pt.bool.isRequired,
  /** Type of the filter group. Can be of type 'radio' or 'checkbox'. */
  type: pt.oneOf(['radio', 'checkbox']),
  /** Whether the filter group should display filter count by the filter name */
  shouldShowCount: pt.bool,
  /** Whether the filter names should have ellipsis at the end when the name is too long*/
  shouldShowEllipsis: pt.bool,
  /** Whether the value used in the URL should be the filter key or filter name */
  shouldUseKeyAsInputValue: pt.bool,
  /** Whether to skip sorting the filters and maintain their original order */
  skipSort: pt.bool,
};

export default FilterGroup;
